import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { clinicalApi } from "../../api/endpoints.js";
import { AdultScreen, AdultHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Loader } from "../../components/Loader.js";
import { QaBanner } from "../../components/QaBanner.js";
import { ErrorBanner, InfoBanner } from "../../components/Form.js";
import { useQuery } from "../../hooks/useQuery.js";
import { useT } from "../../i18n/index.js";
import styles from "./Parent.module.css";

/**
 * Pre-informe de screening del padre. Lee el informe clínico (M11) de la
 * batería PEFF: mientras no haya firma es un PRE-INFORME (nunca diagnóstico) y
 * empuja al marketplace. Una vez validado por un terapeuta, muestra la versión
 * firmada y deja consultar el pre-informe automático como versión anterior.
 * OJO: el parámetro de ruta `:sesionId` transporta un id de BATERÍA.
 */
export function ParentReportPage() {
  const t = useT();
  const navigate = useNavigate();
  const { sesionId = "" } = useParams();
  const { data, loading, error, reload } = useQuery(
    () => clinicalApi.informe(sesionId, "padre"),
    [sesionId],
  );
  const [verPrevio, setVerPrevio] = useState(false);

  const validado = data?.estadoInforme === "validado";
  const title = validado ? t("report.validado.title") : t("report.pre.title");

  function irACompartir(childId: string) {
    navigate(`/terapeutas?compartir=${sesionId}&nino=${childId}`);
  }

  if (loading) {
    return (
      <AdultScreen>
        <AdultHeader title={t("report.pre.title")} />
        <Loader label={t("common.loading")} />
      </AdultScreen>
    );
  }

  if (error || !data) {
    return (
      <AdultScreen>
        <AdultHeader title={t("report.pre.title")} />
        <Content>
          {/* El pre-informe puede no estar listo aún (batería sin procesar).
              Decirlo en claro es mejor que dejar un error crudo. */}
          <InfoBanner tone="warn">
            <b>{t("report.pre.prep.title")}.</b> {t("report.pre.prep.body")}
          </InfoBanner>
          {error ? <ErrorBanner message={error} /> : null}
          <Button variant="ghost" onClick={reload}>
            {t("common.retry")}
          </Button>
          <Button variant="ghost" block onClick={() => navigate("/padre")}>
            {t("common.back")}
          </Button>
        </Content>
      </AdultScreen>
    );
  }

  return (
    <AdultScreen>
      <AdultHeader title={title} />
      <Content>
        {data.modoPrueba ? <QaBanner /> : null}
        {validado && data.validadoPor ? (
          <Card>
            <div className={styles.validBadge}>
              ✔{" "}
              {t("report.validado.badge", {
                nombre: data.validadoPor.nombreProfesional,
                colegiatura: data.validadoPor.numeroColegiatura,
              })}
            </div>
            <p className={styles.kidMeta} style={{ marginTop: 6 }}>
              {t("report.validado.firmadoEn", {
                fecha: new Date(data.validadoPor.firmadaEn).toLocaleDateString(),
              })}
            </p>
          </Card>
        ) : null}

        {validado ? (
          <Card>
            {data.conclusionProfesional ? (
              <>
                <h2>{t("report.conclusion")}</h2>
                <p style={{ whiteSpace: "pre-line" }}>{data.conclusionProfesional}</p>
              </>
            ) : null}
            {data.observaciones ? (
              <>
                <h2 style={{ marginTop: 12 }}>{t("report.observaciones")}</h2>
                <p style={{ whiteSpace: "pre-line" }}>{data.observaciones}</p>
              </>
            ) : null}
          </Card>
        ) : (
          <Card>
            <h2>{data.recomendacion.titulo}</h2>
            <p style={{ whiteSpace: "pre-line", marginTop: 6 }}>{data.recomendacion.mensaje}</p>
          </Card>
        )}

        {/* Versión anterior: el pre-informe automático sigue accesible tras la firma. */}
        {validado && data.preInformePrevioDisponible ? (
          <>
            <Button variant="ghost" block onClick={() => setVerPrevio((v) => !v)}>
              {verPrevio ? t("report.ocultarPrevio") : t("report.verPrevio")}
            </Button>
            {verPrevio ? (
              <Card>
                <h2>{t("report.previo.title")}</h2>
                <p style={{ fontWeight: 700, marginTop: 6 }}>{data.recomendacion.titulo}</p>
                <p style={{ whiteSpace: "pre-line", marginTop: 4 }}>
                  {data.recomendacion.mensaje}
                </p>
              </Card>
            ) : null}
          </>
        ) : null}

        <InfoBanner tone="warn">{data.disclaimer}</InfoBanner>

        {data.ctaMarketplace ? (
          <Button variant="sun" block onClick={() => irACompartir(data.childId)}>
            {data.ctaMarketplace}
          </Button>
        ) : null}

        {!validado ? (
          <Button variant="primary" block onClick={() => irACompartir(data.childId)}>
            {t("report.share")}
          </Button>
        ) : null}

        <Button variant="ghost" block onClick={() => navigate("/padre")}>
          {t("common.back")}
        </Button>
      </Content>
    </AdultScreen>
  );
}
